import { standardLexicon } from '~/boggle/engine/standard/lexicon'
import {
  getSpecialTilePreviewTextColor,
  getSpecialTilePreviewTextShadow,
  normalizeLegacyPathStep,
  resolvePathStepDisplayKind,
} from '~/boggle/engine/specialTiles'
import type { BoggleCompletedRun } from '~/boggle/progress/types'
import { normalizeCompletedRunPathStep, type BoggleCompletedRunAcceptedWordPathStepFactInput } from '~/boggle/progress/acceptedWordFacts'
import {
  deriveBoggleWordLexicalTags,
  normalizeBoggleSubmittedWord,
} from '~/boggle/progress/wordMetadata'
import {
  interWordGapColor,
  OVERTIME_INTER_WORD_GAP_COLOR,
} from '~/boggle/ui/history/interWordGapColor'
import type { BoggleWordListRowItem } from '~/boggle/ui/words/Words'

const DEFAULT_WORD_LETTER_COLOR = 'white'

export function buildColoredLettersFromPathSteps(
  word: string,
  pathSteps: readonly BoggleCompletedRunAcceptedWordPathStepFactInput[],
): BoggleWordListRowItem['coloredLetters'] | undefined {
  if (pathSteps.length === 0) {
    return undefined
  }

  if (pathSteps.length !== word.length) {
    return undefined
  }

  return pathSteps.map((step, index) => {
    const normalized = normalizeLegacyPathStep(normalizeCompletedRunPathStep(step))
    const displayKind = resolvePathStepDisplayKind(normalized)
    return {
      letter: normalized.letter || word[index]!,
      ...(displayKind
        ? {
            color: getSpecialTilePreviewTextColor(displayKind),
            textShadow: getSpecialTilePreviewTextShadow(displayKind),
          }
        : { color: DEFAULT_WORD_LETTER_COLOR }),
    }
  })
}

export function getRunDetailsWordsPlayedSummaryParts(
  items: readonly Pick<BoggleWordListRowItem, 'isReplay'>[],
) {
  const total = items.length
  const repeats = items.filter(item => item.isReplay).length
  const wordLabel = total === 1 ? 'word' : 'words'
  const primary = `${total - repeats} ${wordLabel}`
  if (repeats === 0) {
    return { primary, repeatsSuffix: null }
  }
  const repeatLabel = repeats === 1 ? 'repeat' : 'repeats'
  return { primary, repeatsSuffix: `+ ${repeats} ${repeatLabel}` }
}

export function getRunAcceptedWords(run: BoggleCompletedRun) {
  const timelineWords = run.acceptedWordTimeline?.map(fact => fact.word) ?? []
  return timelineWords.length > 0 ? timelineWords : [...run.submittedWords]
}

export function buildRunDetailsWordItems(run: BoggleCompletedRun): BoggleWordListRowItem[] {
  const seenWords = new Set<string>()
  const timeline = run.acceptedWordTimeline
  if (timeline && timeline.length > 0) {
    return timeline.map((fact, index) => {
      const playedAtSeconds = fact.acceptedAtMs / 1000
      const gapSincePreviousSeconds =
        index > 0
          ? (fact.acceptedAtMs - timeline[index - 1]!.acceptedAtMs) / 1000
          : undefined
      const coloredLetters =
        fact.pathSteps && fact.pathSteps.length > 0
          ? buildColoredLettersFromPathSteps(fact.word, fact.pathSteps)
          : undefined
      const normalizedWord = normalizeBoggleSubmittedWord(fact.word)
      const isReplay = seenWords.has(normalizedWord)
      seenWords.add(normalizedWord)

      return createAcceptedWordListItem(fact.word, index, {
        acceptedDuringOvertime: fact.acceptedDuringOvertime,
        coloredLetters,
        gapSincePreviousSeconds,
        isReplay,
        playedAtSeconds,
      })
    })
  }

  return getRunAcceptedWords(run).map((word, index) => {
    const normalizedWord = normalizeBoggleSubmittedWord(word)
    const isReplay = seenWords.has(normalizedWord)
    seenWords.add(normalizedWord)
    return createAcceptedWordListItem(word, index, { isReplay })
  })
}

function createAcceptedWordListItem(
  word: string,
  index: number,
  timing?: {
    acceptedDuringOvertime?: boolean
    coloredLetters?: BoggleWordListRowItem['coloredLetters']
    gapSincePreviousSeconds?: number
    isReplay?: boolean
    playedAtSeconds?: number
  },
): BoggleWordListRowItem {
  const normalizedWord = normalizeBoggleSubmittedWord(word)

  return {
    displayWord: word,
    length: word.length,
    ...(timing?.isReplay ? { isReplay: true } : {}),
    lexicalTags: deriveBoggleWordLexicalTags(normalizedWord, standardLexicon),
    normalizedWord: `${normalizedWord}-${index}`,
    ...(timing?.coloredLetters ? { coloredLetters: timing.coloredLetters } : {}),
    ...(timing?.gapSincePreviousSeconds !== undefined
      ? {
          gapSincePreviousColor: timing.acceptedDuringOvertime
            ? OVERTIME_INTER_WORD_GAP_COLOR
            : interWordGapColor(timing.gapSincePreviousSeconds),
          gapSincePreviousSeconds: timing.gapSincePreviousSeconds,
        }
      : {}),
    ...(timing?.playedAtSeconds !== undefined
      ? { playedAtSeconds: timing.playedAtSeconds }
      : {}),
  }
}
